import type { Question, Submission } from '@l2u/ui/lib/types.ts';
import { expiryState } from '@l2u/ui/lib/expiry.ts';
import { documentAwaitingVendor, isDocumentsTabDocument } from '@l2u/ui/lib/documentsTab.ts';

// Which Documents-tab documents need the vendor's attention. ONE helper for the
// Documents nav badge (CompanyShell), the Documents page's "Needs your
// attention" group and the Home action list, so the three can never disagree:
// every caller passes the same submissions, questions and standard documents.
//
// Counted per DOCUMENT (submission), not per file: two expired files on one
// document are one thing to deal with.

/**
 * A document's live (not superseded) files: one Documents-page card each. The
 * /api/me read paths already drop superseded files; the filter is defensive,
 * and shared so the page's cards and the badge's count read the same files.
 */
export function liveFiles<F extends { superseded_at?: string | null }>(sub: {
  submission_files: readonly F[];
}): F[] {
  return sub.submission_files.filter((f) => !f.superseded_at);
}

/** A Documents-tab document shows at least one card: a live file, or an adoption whose standard document still exists. */
export function documentHasCard(
  sub: Pick<Submission, 'submission_files' | 'submission_adoptions'>,
  docById: ReadonlyMap<string, unknown>,
): boolean {
  if (liveFiles(sub).length > 0) return true;
  // Mirrors the Documents page: only an 'adopted' adoption whose standard
  // document is still listed renders a card.
  return sub.submission_adoptions.some((a) => a.mode === 'adopted' && docById.has(a.document_id));
}

/** Any live file expired (expiryState === 'expired'), or the vendor owes a reply. Per DOCUMENT, not per file. */
export function documentNeedsAttention(sub: Submission, now: Date): boolean {
  const anyExpired = liveFiles(sub).some((f) => expiryState(f, now) === 'expired');
  return anyExpired || documentAwaitingVendor(sub);
}

/** Submission ids, in input order, of Documents-tab documents that render a card and need attention. */
export function documentsNeedingAttention(
  submissions: Submission[],
  questionById: ReadonlyMap<string, Question>,
  docById: ReadonlyMap<string, unknown>,
  now: Date = new Date(),
): string[] {
  return submissions
    .filter(
      (s) =>
        isDocumentsTabDocument(questionById.get(s.question_id), s) &&
        documentHasCard(s, docById) &&
        documentNeedsAttention(s, now),
    )
    .map((s) => s.id);
}
